// Recurring JE intent — Grant C. Records a request; does not write beancount.
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { AdminApiError, type AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import type {
  CompanySlug,
  RecurringAdminGet,
  RecurringRunRequestView,
  RecurringScheduleItem,
  StaffRole,
} from "../types.js";
import { button, disclosure, pageHeader } from "../ui/components.js";
import * as face from "../ui/face.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { runWrite, type WriteOutcome } from "../ui/write.js";
import {
  accountLabel,
  moneyText,
  pluralText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import { createPeriodControl, defaultPeriod, formatPeriodLabel } from "../ui/period-control.js";

export interface RecurringPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /** Signed-in role, for the shared failure sentences. */
  role?: StaffRole;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1): a posting summary names the accounts
   * the books will write.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

function kindLabel(kind: RecurringScheduleItem["kind"]): string {
  return kind === "loan" ? "Loan" : "Repeating";
}

/**
 * The stored posting summary as a sentence. The pipeline writes two shapes:
 * `Account amount; Account amount` for a repeating entry, and a loan line
 * (`payment 1200.00 principal … (Account / Account)`). Both carry colon paths
 * and raw decimals, so both are rewritten: the path through `accountLabel`
 * and each amount through `moneyText`. Text that matches neither shape is
 * returned unchanged, so an unfamiliar summary is never mangled.
 */
export function postingSummaryText(
  summary: string,
  companies: AccountLabelContext,
): string {
  // Stored text is untrusted: hazards are replaced before it is parsed or shown.
  const text = amVisibleDisplayText(summary).trim();
  if (text === "") return text;
  const parsed: Array<{ account: string; amount: string }> = [];
  for (const part of text.split(LEG_SEPARATOR)) {
    const leg = LEG_PATTERN.exec(part.trim());
    if (leg === null) {
      parsed.length = 0;
      break;
    }
    parsed.push({ account: leg[1] ?? "", amount: leg[2] ?? "" });
  }
  if (parsed.length >= 2) return legsSentence(parsed, companies);
  // A loan summary, or any other shape: rewrite the paths and the amounts it
  // does carry and leave the words alone.
  return rewritePathsAndAmounts(text, companies);
}

/** One `Account amount` leg: a colon path, then a decimal amount. */
const LEG_PATTERN = /^([A-Za-z][A-Za-z0-9:_-]*)\s+(-?\d+(?:\.\d+)?)$/;
const LEG_SEPARATOR = /\s*[;/]\s*/;
const PATH_TOKEN = /\b[A-Z][A-Za-z0-9]*(?::[A-Za-z0-9][A-Za-z0-9_-]*)+\b/g;

/**
 * A posting leg as a person reads it: the ledger head and the company code are
 * internal keys, so `Expenses:SYN:Rent` is `Rent` and `Assets:SYN:Wallet:`
 * `Operating` is `Wallet › Operating`. Both drops belong to the one labeller
 * (rd Y0 + Y2); nothing is re-implemented here.
 */
function legLabel(path: string, companies: AccountLabelContext): string {
  return accountLabel(path, companies, { dropLeading: "ledgerRoot" });
}
/**
 * A money amount, not any bare integer: a decimal is required, so hostile text
 * such as `<img onerror=alert(1)>` is never rewritten into `$1.00`.
 */
const AMOUNT_TOKEN = /(?<![\w$.−-])-?\d[\d,]*\.\d{1,2}(?![\d])/g;

function rewritePathsAndAmounts(
  text: string,
  companies: AccountLabelContext,
): string {
  return text
    .replace(PATH_TOKEN, (path) => legLabel(path, companies))
    .replace(AMOUNT_TOKEN, (amount) => moneyText(amount, { fallback: amount }));
}

/**
 * `Expenses:Rent 100.00 / Assets:Wallet:Operating -100.00` reads as
 * `Rent $100.00 from Wallet › Operating`: what the money was for, and where it
 * came from.
 *
 * The decided shape assumes the first leg is the positive one. A loan payment
 * often lists the liability first (`Liabilities:Loans -250.00 / Expenses:
 * Interest 12.50`), and reading that as "Loans −$250.00 from Interest" says the
 * interest paid the loan — backwards. So the subject is the positive legs and
 * the source is the negative ones, whatever order the summary is in; the words
 * are the same two ("from") the decided sentence uses.
 */
function legsSentence(
  legs: ReadonlyArray<{ account: string; amount: string }>,
  companies: AccountLabelContext,
): string {
  const first = legs[0];
  if (first === undefined) return "";
  const isCredit = (leg: { amount: string }): boolean => leg.amount.trim().startsWith("-");
  const positives = legs.filter((leg) => !isCredit(leg));
  const negatives = legs.filter((leg) => isCredit(leg));
  // Every leg is a credit: there is no "what it was for", so the first leg
  // stays the subject and the rest is where it came from.
  const subject = positives.length > 0 ? positives : [first];
  const source = positives.length > 0 ? negatives : legs.slice(1);
  const head = subject
    .map(
      (leg) =>
        `${legLabel(leg.account, companies)} ${moneyText(leg.amount, { fallback: leg.amount })}`,
    )
    .join(" and ");
  const rest = source.map((leg) => legLabel(leg.account, companies));
  if (rest.length === 0) return head;
  const tail =
    rest.length === 1
      ? rest[0] ?? ""
      : `${rest.slice(0, -1).join(", ")} and ${rest[rest.length - 1] ?? ""}`;
  return `${head} from ${tail}`;
}

function paintLastRun(host: HTMLElement, request: RecurringRunRequestView | undefined): void {
  clear(host);
  if (request === undefined) {
    host.append(
      document.createTextNode(
        "No run has been requested yet. Choose a month, then use Generate this period.",
      ),
    );
    return;
  }
  if (request.status === "pending") {
    host.append(
      document.createTextNode(
        `Period ${amVisibleDisplayText(formatPeriodLabel(request.period))}: requested — waiting for the next books update. ` +
          "Entries are not in the books until this request shows succeeded.",
      ),
    );
    return;
  }
  if (request.status === "succeeded") {
    host.append(
      document.createTextNode(
        `Period ${amVisibleDisplayText(formatPeriodLabel(request.period))}: succeeded. ` +
          `${pluralText(request.entriesWritten ?? 0, "entry was", "entries were")} written and ` +
          `${pluralText(request.entriesSkipped ?? 0, "entry was", "entries were")} skipped.`,
      ),
    );
    return;
  }
  const code = request.errorCode ?? "";
  const face =
    code === "period_locked"
      ? `Period ${amVisibleDisplayText(formatPeriodLabel(request.period))} failed because that month is locked. Nothing was written.`
      : `Period ${amVisibleDisplayText(formatPeriodLabel(request.period))} failed. Nothing was written. Try again after the problem is fixed.`;
  host.append(document.createTextNode(face));
  if (code !== "") {
    host.append(disclosure("Show details", h("p", {}, [amVisibleDisplayText(code)])));
  }
}

function scheduleRow(
  item: RecurringScheduleItem,
  companies: AccountLabelContext,
): HTMLTableRowElement {
  const day = item.day === null ? "—" : String(item.day);
  return h("tr", {}, [
    h("td", { "data-label": "Name" }, [amVisibleDisplayText(item.name)]),
    h("td", { "data-label": "Kind" }, [kindLabel(item.kind)]),
    h("td", { "data-label": "Enabled" }, [item.enabled ? "On" : "Off"]),
    h("td", { "data-label": "Day" }, [day]),
    h("td", { "data-label": "Narration" }, [amVisibleDisplayText(item.narration)]),
    h("td", { "data-label": "Location" }, [amVisibleDisplayText(item.location)]),
    h("td", { "data-label": "Posting summary", "data-role": "posting-summary" }, [
      postingSummaryText(item.postingSummary, companies),
    ]),
  ]);
}

export async function renderRecurringPage(opts: RecurringPageOptions): Promise<void> {
  const { client, company, root } = opts;
  const role = readerRole(opts.role);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  clear(root);

  let period = defaultPeriod();
  let snapshot: RecurringAdminGet | null = null;

  const header = pageHeader(
    "Recurring",
    "Schedule last saved from the books. This page does not edit that schedule. " +
      "Generate records a request. Entries exist only after a later refresh shows succeeded.",
  );
  header.querySelector("h1")?.setAttribute("data-role", "recurring-title");
  header.querySelector("p")?.setAttribute("data-role", "recurring-lede");
  root.append(header);

  const live = h("div", {
    class: "banner",
    role: "status",
    "data-role": "recurring-live",
    hidden: "true",
  });
  root.append(live);

  const periodHost = h("div", { class: "toolbar", "data-role": "recurring-toolbar" });
  periodHost.append(
    createPeriodControl({
      period,
      ariaLabel: "Recurring period",
      onChange: (next) => {
        period = next;
      },
    }),
  );
  const generate = button("Generate this period", "primary", {
    "data-action": "generate-recurring",
  });
  periodHost.append(generate);
  root.append(periodHost);

  const lastRun = h("div", { class: "card", "data-role": "recurring-last-run" }, ["Loading…"]);
  root.append(lastRun);

  const scheduleHost = h("div", { class: "recurring-schedule", "data-role": "recurring-schedule" });
  root.append(scheduleHost);

  async function load(): Promise<void> {
    const data = await client.getRecurring(company);
    if (!stillCurrent()) return;
    snapshot = data;
    paint();
  }

  function paint(): void {
    const data = snapshot;
    if (data === null) return;
    const newest = data.requests[0];
    paintLastRun(lastRun, newest);

    clear(scheduleHost);
    if (!data.present || data.schedule.length === 0) {
      scheduleHost.append(
        h("p", { class: "empty empty-state", "data-role": "recurring-empty" }, [
          "No schedule has been saved yet. Save the repeating entries in the books, then refresh this page.",
        ]),
      );
      return;
    }
    const table = h("table", { class: "data-table", "data-role": "recurring-table" });
    table.append(
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Name"]),
          h("th", {}, ["Kind"]),
          h("th", {}, ["Enabled"]),
          h("th", {}, ["Day"]),
          h("th", {}, ["Narration"]),
          h("th", {}, ["Location"]),
          h("th", {}, ["Posting summary"]),
        ]),
      ]),
    );
    const tbody = h("tbody", {});
    for (const item of data.schedule) {
      tbody.append(scheduleRow(item, companies));
    }
    table.append(tbody);
    scheduleHost.append(h("div", { class: "table-scroll card", "data-role": "data-table" }, [table]));
  }

  /** The live line stays out of the layout until it has something to say. */
  function showLive(): void {
    live.hidden = false;
    live.removeAttribute("hidden");
  }

  function paintLoadFailure(error: unknown): void {
    clear(live);
    showLive();
    live.className = "error-state";
    live.setAttribute("role", "alert");
    const copy = failureCopy(error, { role, context: "read" });
    live.append(document.createTextNode(copy.sentence));
    if (copy.detail !== null) {
      live.append(disclosure("Show details", h("p", {}, [copy.detail])));
    }
    const retry = button("Try again", "primary", {
      "data-action": "retry-recurring",
      "aria-label": "Try again",
    });
    retry.addEventListener("click", () => {
      void (async () => {
        try {
          clear(live);
          showLive();
          live.className = "banner";
          live.setAttribute("role", "status");
          live.append(document.createTextNode("Loading…"));
          await load();
        } catch (retryError) {
          if (!stillCurrent()) return;
          paintLoadFailure(retryError);
        }
      })();
    });
    live.append(retry);
  }

  /** A write outcome's one sentence, plus the shared retry (dispatch-b §3.1). */
  function paintWriteFailure(outcome: WriteOutcome): void {
    clear(live);
    showLive();
    live.className = "error-state";
    live.setAttribute("role", "alert");
    const copy =
      outcome.kind !== "landed" && outcome.error !== undefined
        ? failureCopy(outcome.error, { role, context: "write" })
        : { sentence: outcomeSentence(outcome, role), detail: null };
    live.append(document.createTextNode(copy.sentence));
    if (copy.detail !== null) {
      live.append(disclosure("Show details", h("p", {}, [copy.detail])));
    }
    const retry = button("Try again", "primary", {
      "data-action": "regen-recurring",
      "aria-label": "Try again",
    });
    retry.addEventListener("click", () => {
      void requestRun();
    });
    live.append(retry);
  }

  function paintListReloadFailed(success: string): void {
    clear(live);
    showLive();
    live.className = "error-state";
    live.setAttribute("role", "alert");
    live.append(
      document.createTextNode(
        `${success} The list did not reload. Try again to load it; the request is already recorded.`,
      ),
    );
    const retry = button("Try again", "secondary", {
      "data-action": "retry-recurring-list",
      "aria-label": "Try again",
    });
    retry.addEventListener("click", () => {
      void (async () => {
        try {
          clear(live);
          showLive();
          live.className = "banner";
          live.setAttribute("role", "status");
          live.append(document.createTextNode("Loading…"));
          await load();
        } catch (retryError) {
          if (!stillCurrent()) return;
          paintLoadFailure(retryError);
        }
      })();
    });
    live.append(retry);
  }

  async function requestRun(): Promise<void> {
    const outcome = await runWrite({
      key: "recurring-run",
      scope: periodHost,
      gate: () => null,
      send: () => client.requestRecurringRun(company, period),
      // The re-read is part of the landed write: its failure is the list's,
      // never "the request did not save" (rd round 12, A5-1).
      afterLanded: () => load(),
      stillCurrent,
    });
    if (!stillCurrent()) return;
    if (outcome.kind !== "landed") {
      paintWriteFailure(outcome);
      return;
    }
    if (outcome.afterLandedFailed === true) {
      paintListReloadFailed("Request recorded.");
      return;
    }
    clear(live);
    showLive();
    live.className = "banner";
    live.setAttribute("role", "status");
    live.append(
      document.createTextNode(
        "Request recorded. Entries are not in the books until this request shows succeeded.",
      ),
    );
  }

  generate.addEventListener("click", () => {
    // One press, one request: the write gate refuses a second press while the
    // first is in flight (rd round 12, A5-1).
    void requestRun();
  });

  try {
    await load();
  } catch (error) {
    if (!stillCurrent()) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    paintLoadFailure(error);
    clear(lastRun);
  }
}
